import { useEffect, useRef, useState } from "react";
import {
  JOB_TYPE_LABELS,
  CANCELLATION_REASON_LABELS,
  type Job,
  type Driver,
  type Truck,
  type CancellationReason,
} from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";
import FieldError from "../../components/FieldError";
import type { DispatcherRoute } from "./DispatchHome";

const CANCELLATION_REASONS = Object.keys(
  CANCELLATION_REASON_LABELS,
) as CancellationReason[];

function money(cents: number | null): string {
  if (cents === null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function JobDetail({
  id,
  onNavigate,
  onToast,
}: {
  id: string;
  onNavigate: (route: DispatcherRoute) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Action Panel State
  // Mode: null | "assign" | "reassign_confirm" | "cancel" | "pause"
  const [actionPanel, setActionPanel] = useState<
    "assign" | "reassign_confirm" | "cancel" | "pause" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Assign Form State
  const assignLockRef = useRef(false);
  // Idempotency keys — one UUID per user intent, held across retries so
  // a repeated click replays the SAME key and dedups server-side
  // (same pattern as the driver screens' start/finish refs).
  const assignKeyRef = useRef<string | null>(null);
  const reassignKeyRef = useRef<string | null>(null);
  const pauseKeyRef = useRef<string | null>(null);
  const resumeKeyRef = useRef<string | null>(null);
  const cancelKeyRef = useRef<string | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [assignDriverId, setAssignDriverId] = useState("");
  const [assignTruckId, setAssignTruckId] = useState("");

  // Cancel Form State
  const [cancelReason, setCancelReason] = useState<CancellationReason | "">("");
  const [cancelNote, setCancelNote] = useState("");

  const loadJob = () => {
    apiFetch<Job>(`/api/v1/jobs/${id}`)
      .then((j) => {
        setJob(j);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    loadJob();
  }, [id]);

  // Load drivers & trucks when opening the assign panel
  const openAssignPanel = () => {
    setActionPanel("assign");
    setActionError(null);
    setFieldErrors({});
    setAssignDriverId("");
    setAssignTruckId(job?.truck?.id ?? "");

    apiFetch<Driver[]>("/api/v1/drivers?available=true")
      .then(setDrivers)
      .catch(() => {});
    apiFetch<Truck[]>("/api/v1/trucks")
      .then(setTrucks)
      .catch(() => {});
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    setActionLoading(true);
    setActionError(null);
    setFieldErrors({});

    const truckRequired = job.truck === null;
    if (!assignDriverId) {
      setFieldErrors({ driverId: "Driver is required" });
      setActionLoading(false);
      return;
    }
    if (truckRequired && !assignTruckId) {
      setFieldErrors({ truckId: "Truck is required (job has no assigned truck)" });
      setActionLoading(false);
      return;
    }

    try {
      if (assignLockRef.current) return;
      assignLockRef.current = true;
      const payload: { driverId: string; truckId?: string } = {
        driverId: assignDriverId,
      };
      if (assignTruckId) {
        payload.truckId = assignTruckId;
      }

      if (!assignKeyRef.current) assignKeyRef.current = crypto.randomUUID();
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/assign`, {
        method: "POST",
        headers: { "Idempotency-Key": assignKeyRef.current },
        body: JSON.stringify(payload),
      });

      assignKeyRef.current = null;
      setActionPanel(null);
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} assigned to driver.`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          // Transition errors carry the explanation in body.reason
          // (assign route sends {error:"invalid_transition", reason:"…"}).
          const reason =
            typeof err.body.reason === "string" ? err.body.reason : err.message;
          setActionError(reason);
          if (err.code === "invalid_transition") {
            // The job changed server-side (e.g. a first click already
            // assigned it) — close the assign panel explicitly (it gates on
            // actionPanel, NOT job.driver) and refetch so the UI reflects
            // reality instead of showing a stale unassigned form.
            setActionPanel(null);
            setFieldErrors({});
            loadJob();
          }
        }
      } else {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setActionLoading(false);
      assignLockRef.current = false;
    }
  };

  const handleReassign = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      if (!reassignKeyRef.current) reassignKeyRef.current = crypto.randomUUID();
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/reassign`, {
        method: "POST",
        headers: { "Idempotency-Key": reassignKeyRef.current },
        body: JSON.stringify({}),
      });

      reassignKeyRef.current = null;
      setActionPanel(null);
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} unassigned and returned to NEW.`);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    if (!cancelReason) {
      setFieldErrors({ reason: "Reason is required" });
      return;
    }
    if (cancelReason === "OTHER_WITH_NOTE" && !cancelNote.trim()) {
      setFieldErrors({ note: "Note is required for 'Other' reason" });
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setFieldErrors({});

    try {
      if (!cancelKeyRef.current) cancelKeyRef.current = crypto.randomUUID();
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/cancel`, {
        method: "POST",
        headers: { "Idempotency-Key": cancelKeyRef.current },
        body: JSON.stringify({
          reason: cancelReason,
          note: cancelNote.trim() ? cancelNote.trim() : undefined,
        }),
      });

      cancelKeyRef.current = null;
      if (onToast) onToast(`Job ${job.jobNumber} cancelled.`);
      onNavigate({ name: "jobs" });
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setActionError(err.message);
        }
      } else {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Pause/Resume state (Unit #25)
  const [pauseNote, setPauseNote] = useState("");

  const handlePause = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      if (!pauseKeyRef.current) pauseKeyRef.current = crypto.randomUUID();
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/pause`, {
        method: "POST",
        headers: { "Idempotency-Key": pauseKeyRef.current },
        body: JSON.stringify({ note: pauseNote.trim() || undefined }),
      });

      pauseKeyRef.current = null;
      setActionPanel(null);
      setPauseNote("");
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} paused — driver freed.`);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!job) return;
    setActionLoading(true);
    setActionError(null);

    try {
      if (!resumeKeyRef.current) resumeKeyRef.current = crypto.randomUUID();
      await apiFetch<Job>(`/api/v1/jobs/${job.id}/resume`, {
        method: "POST",
        headers: { "Idempotency-Key": resumeKeyRef.current },
        body: JSON.stringify({}),
      });

      resumeKeyRef.current = null;
      setActionPanel(null);
      loadJob();
      if (onToast) onToast(`Job ${job.jobNumber} resumed — driver restored.`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setFieldErrors(err.fieldErrors);
        } else {
          setActionError(err.message);
        }
      } else {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const back = () => onNavigate({ name: "jobs" });

  if (error) {
    return (
      <main className="surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back
          </Button>
          <h1>Job</h1>
        </header>
        <Card>
          <p className="err">Failed to load job.</p>
          <p className="muted">{error}</p>
        </Card>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back
          </Button>
          <h1>Job</h1>
        </header>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  const snap = (job.customerSnapshot ?? {}) as {
    customer?: Record<string, unknown>;
    property?: Record<string, unknown>;
  };
  const customer = (snap.customer ?? {}) as Record<string, unknown>;
  const property = (snap.property ?? {}) as Record<string, unknown>;

  const address = [property.addressLine1, property.city, property.state, property.zip]
    .filter(Boolean)
    .join(", ");

  const isUnassigned = job.state === "NEW" && !job.driver;
  const canReassign = (job.state === "NEW" && !!job.driver) || job.state === "IN_PROGRESS" || job.state === "PAUSED";
  const canCancel = job.state === "NEW" || job.state === "IN_PROGRESS" || job.state === "PAUSED";
  const canPause = job.state === "IN_PROGRESS";
  const canResume = job.state === "PAUSED";

  return (
    <main className="surface">
      <header className="surface-header">
        <Button variant="ghost" onClick={back}>
          ← Back
        </Button>
        <h1>{job.jobNumber}</h1>
        <StatusTag state={job.state} />
      </header>

      {/* Dispatcher Actions Panel */}
      {(isUnassigned || canReassign || canCancel || canPause || canResume) && (
        <Card>
          <h2>Job Actions</h2>

          {actionError && <p className="form-error err">{actionError}</p>}

          {/* Default Button Bar */}
          {actionPanel === null && (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
              {isUnassigned && (
                <Button variant="primary" onClick={openAssignPanel}>
                  Assign Driver &amp; Truck…
                </Button>
              )}
              {canReassign && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setActionPanel("reassign_confirm");
                    setActionError(null);
                  }}
                >
                  Reassign Job…
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  style={{ color: "var(--red)" }}
                  onClick={() => {
                    setActionPanel("cancel");
                    setActionError(null);
                    setFieldErrors({});
                  }}
                >
                  Cancel Job…
                </Button>
              )}
              {canPause && (
                <Button
                  variant="secondary"
                  style={{ color: "#8A6D00" }}
                  onClick={() => {
                    setActionPanel("pause");
                    setActionError(null);
                    setPauseNote("");
                  }}
                >
                  Pause Job…
                </Button>
              )}
              {canResume && (
                <Button
                  variant="primary"
                  onClick={handleResume}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Resuming…" : "Resume Job"}
                </Button>
              )}
            </div>
          )}

          {/* ASSIGN PANEL */}
          {actionPanel === "assign" && (
            <form onSubmit={handleAssign} className="admin-inline-form" style={{ marginTop: "10px" }}>
              <h3>Assign Driver &amp; Truck</h3>
              <div className="form-group">
                <label>Select Driver*</label>
                <select
                  value={assignDriverId}
                  onChange={(e) => setAssignDriverId(e.target.value)}
                  disabled={actionLoading}
                >
                  <option value="">Choose an available driver…</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.phone ? `(${d.phone})` : ""}
                    </option>
                  ))}
                </select>
                {drivers.length === 0 && <span className="muted">No drivers currently available.</span>}
                <FieldError message={fieldErrors.driverId} />
              </div>

              <div className="form-group">
                <label>
                  Select Truck{job.truck === null ? "*" : " (optional, change truck)"}
                </label>
                <select
                  value={assignTruckId}
                  onChange={(e) => setAssignTruckId(e.target.value)}
                  disabled={actionLoading}
                >
                  <option value="">
                    {job.truck
                      ? `Keep current (Truck ${job.truck.unitNumber})`
                      : "Choose a truck…"}
                  </option>
                  {trucks.map((t) => (
                    <option key={t.id} value={t.id}>
                      Truck {t.unitNumber} {[t.make, t.model].filter(Boolean).join(" ")}
                    </option>
                  ))}
                </select>
                {job.truck === null && (
                  <span className="muted" style={{ fontSize: "12px" }}>
                    This job was created unassigned without a truck. Truck selection is required.
                  </span>
                )}
                <FieldError message={fieldErrors.truckId} />
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" variant="primary" disabled={actionLoading}>
                  {actionLoading ? "Assigning…" : "Assign & Send"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* REASSIGN CONFIRM PANEL */}
          {actionPanel === "reassign_confirm" && (
            <div className="banner banner-warning" style={{ marginTop: "10px" }}>
              <p style={{ margin: "0 0 10px 0" }}>
                <strong>Pull job back from {job.driver?.name || "driver"}?</strong>
                <br />
                The job will return to <code>NEW</code> and unassigned. The driver's availability will be restored.
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="secondary"
                  onClick={handleReassign}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Reassigning…" : "Yes, Pull Back"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* CANCEL PANEL */}
          {actionPanel === "cancel" && (
            <form onSubmit={handleCancel} className="admin-inline-form" style={{ marginTop: "10px" }}>
              <h3>Cancel Job</h3>
              <div className="form-group">
                <label>Cancellation Reason*</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value as CancellationReason)}
                  disabled={actionLoading}
                >
                  <option value="">Select a reason…</option>
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {CANCELLATION_REASON_LABELS[r]}
                    </option>
                  ))}
                </select>
                <FieldError message={fieldErrors.reason} />
              </div>

              {cancelReason === "OTHER_WITH_NOTE" && (
                <div className="form-group">
                  <label>Cancellation Note*</label>
                  <textarea
                    rows={2}
                    value={cancelNote}
                    onChange={(e) => setCancelNote(e.target.value)}
                    placeholder="Provide details for cancellation…"
                    disabled={actionLoading}
                  />
                  <FieldError message={fieldErrors.note} />
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button
                  type="submit"
                  variant="primary"
                  style={{ background: "var(--red)", borderColor: "var(--red)", color: "#ffffff" }}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Cancelling…" : "Confirm Cancel"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Back
                </Button>
              </div>
            </form>
          )}

          {/* PAUSE PANEL — optional note (Unit #25) */}
          {actionPanel === "pause" && (
            <div style={{ marginTop: "10px" }}>
              <h3>Pause Job</h3>
              <p className="muted" style={{ fontSize: "13px", marginBottom: "8px" }}>
                This frees the driver immediately so they can take a priority job.
                The same driver will be restored when you resume.
              </p>
              <div className="form-group">
                <label>Note (optional)</label>
                <textarea
                  rows={2}
                  value={pauseNote}
                  onChange={(e) => setPauseNote(e.target.value)}
                  placeholder="e.g. redirecting to ED-0042"
                  disabled={actionLoading}
                  maxLength={500}
                />
              </div>
              {actionError && <p className="form-error err">{actionError}</p>}
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button
                  variant="secondary"
                  style={{ color: "#8A6D00" }}
                  onClick={handlePause}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Pausing…" : "Pause Job"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setActionPanel(null)}
                  disabled={actionLoading}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card>
        <h2>Customer</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Company</dt>
            <dd>{String(customer.companyName ?? "—")}</dd>
          </div>
          <div className="detail-row">
            <dt>Site</dt>
            <dd>{String(property.siteName ?? "—")}</dd>
          </div>
          <div className="detail-row">
            <dt>Address</dt>
            <dd>{address || "—"}</dd>
          </div>
        </dl>
      </Card>

      <Card>
        <h2>Job Details</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Size</dt>
            <dd>{job.dumpsterSize ? `${job.dumpsterSize.label} (${job.dumpsterSize.yards} yd)` : "—"}</dd>
          </div>
          <div className="detail-row">
            <dt>Type</dt>
            <dd>{JOB_TYPE_LABELS[job.jobType]}</dd>
          </div>
          <div className="detail-row">
            <dt>Truck</dt>
            <dd>{job.truck ? `Truck ${job.truck.unitNumber}` : "Unassigned"}</dd>
          </div>
          <div className="detail-row">
            <dt>Driver</dt>
            <dd>{job.driver?.name ?? "Unassigned"}</dd>
          </div>
        </dl>
      </Card>

      {job.message ? (
        <Card>
          <h2>Dispatcher Message</h2>
          <p className="job-message">{job.message}</p>
        </Card>
      ) : null}

      {/* Unit #26: Load Decision — read-only for dispatcher */}
      {(job.loadDecision || job.loadNote) && (
        <Card>
          <h2>Load Decision</h2>
          {job.loadDecision && (
            <div
              className={job.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
              style={{ marginBottom: "8px", display: "inline-block" }}
            >
              {job.loadDecision === "LANDFILL" ? "Load Decision: LANDFILL" : "Load Decision: FACILITY METAL"}
            </div>
          )}
          {job.loadNote && <p className="muted">{job.loadNote}</p>}
        </Card>
      )}

      <Card>
        <h2>Timeline</h2>
        <dl className="detail-list">
          <div className="detail-row">
            <dt>Created</dt>
            <dd>{formatDateTime(job.createdAt)}</dd>
          </div>
          <div className="detail-row">
            <dt>Started</dt>
            <dd>{formatDateTime(job.startAt)}</dd>
          </div>
          <div className="detail-row">
            <dt>Finished</dt>
            <dd>{formatDateTime(job.finishAt)}</dd>
          </div>
        </dl>
      </Card>

      {job.state === "FINISHED" || job.state === "REVIEWED" ? (
        <Card>
          <h2>Money</h2>
          <dl className="detail-list">
            <div className="detail-row">
              <dt>Weight</dt>
              <dd>{job.weightTons === null ? "—" : `${job.weightTons} tons`}</dd>
            </div>
            <div className="detail-row">
              <dt>Disposal</dt>
              <dd>{money(job.disposalAmountCents)}</dd>
            </div>
            <div className="detail-row">
              <dt>Billable</dt>
              <dd>{money(job.billableAmountCents)}</dd>
            </div>
          </dl>
        </Card>
      ) : null}
    </main>
  );
}
